import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Button,
    Stack,
    CircularProgress,
    Container,
    useTheme,
    useMediaQuery,
    Tooltip,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';

import {
    getEncounter,
    updateEncounter,
    finalizeEncounter,
    amendEncounter,
    completeAmendment,
    listAssignablePhysicians,
} from '../../../api/encounter';
import { apiGet } from '../../../api/client';
import useDebouncedAutosave from '../../../hooks/useDebouncedAutosave';
import useUnsavedGuard from '../../../hooks/useUnsavedGuard';
import { usePermissions } from '../../../hooks/usePermissions';

import PatientBanner from './PatientBanner';
import TabRail from './TabRail';
import FinalizeDialog, { buildChecklist } from './FinalizeDialog';
import AmendDialog from './AmendDialog';
import { isTabComplete } from './tabCompletion';

import PatientInfoTab from './tabs/PatientInfoTab';
import AllergiesTab from './tabs/AllergiesTab';
import VitalsTab from './tabs/VitalsTab';
import SimpleTextTab from './tabs/SimpleTextTab';
import ChipListTab from './tabs/ChipListTab';
import PreviousInvestigationsTab from './tabs/PreviousInvestigationsTab';
import DocumentsTab from './tabs/DocumentsTab';
import PhysicalExamTab from './tabs/PhysicalExamTab';
import DiagnosisTab from './tabs/DiagnosisTab';
import InvestigationTab from './tabs/InvestigationTab';
import TreatmentTab from './tabs/TreatmentTab';
import WorkStatusTab from './tabs/WorkStatusTab';
import SummaryTab from './tabs/SummaryTab';

const NAVY = '#052F5D';

const TAB_DEFS = [
    { key: 'patient', label: 'Patient information' },
    { key: 'allergies', label: 'Allergies' },
    { key: 'vitals', label: 'Vitals' },
    { key: 'chiefComplaint', label: 'Chief complaint' },
    { key: 'hopi', label: 'History of presenting illness' },
    { key: 'currentMedications', label: 'Current medication' },
    { key: 'pmh', label: 'Past medical history' },
    { key: 'psh', label: 'Past surgical history' },
    { key: 'previousInvestigations', label: 'Previous investigations' },
    { key: 'documents', label: 'Documents' },
    { key: 'physicalExam', label: 'Physical examination' },
    { key: 'diagnoses', label: 'Diagnosis' },
    { key: 'investigations', label: 'Investigations' },
    { key: 'treatment', label: 'Treatment' },
    { key: 'workStatus', label: 'Work status' },
    { key: 'summary', label: 'Patient summary' },
];

// Finalizing is its own grantable permission (Settings → Permissions →
// "Finalize encounters"; admin always has it), while day-to-day editing
// rights come from the role's "encounters" permission so a role can be
// granted read-only access from the Permissions page. Reopening a signed
// report is a third permission ("Amend signed reports") for the same reason.

// The open amendment, if any — the one entry with no closedAt. Mirrors
// openAmendment() in server/controllers/encounter/index.js.
function findOpenAmendment(encounter) {
    return (encounter?.amendments || []).find((a) => !a.closedAt) || null;
}

export default function EncounterEditor() {
    const { encounterId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const { canWrite: canWritePerm, can } = usePermissions();
    const canWrite = canWritePerm('encounters');
    // NOTE: these must ask for ':write', not canAccess(). canAccess(m) checks
    // "m:read", and finalizeEncounters / amendEncounters are WRITE-ONLY
    // capabilities — "finalizeEncounters:read" does not exist in the catalog,
    // so canAccess() here is always false and the buttons never render for
    // anyone but an admin (who short-circuits on role).
    const canFinalizePerm = can('finalizeEncounters:write');
    const canAmendPerm = can('amendEncounters:write');

    const [encounter, setEncounter] = useState(null);
    const [patient, setPatient] = useState(null);
    const [physicians, setPhysicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(0);
    const [finalizeOpen, setFinalizeOpen] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [amendOpen, setAmendOpen] = useState(false);
    const [amending, setAmending] = useState(false);
    const [amendError, setAmendError] = useState(null);

    const isFinalized = encounter?.status && encounter.status !== 'draft';
    // A signed encounter with an amendment open is editable again, but it is
    // still finalized — the visit was signed, and an in-progress correction
    // does not un-sign it. Every "can I type here" check keys off isEditable.
    const amendment = findOpenAmendment(encounter);
    const isAmending = Boolean(amendment);
    const isEditable = !isFinalized || isAmending;

    // ---- Autosave ----
    // Ref so onSave can reach the autosave handle (getPending) without a
    // use-before-define cycle — assigned right after the hook returns.
    const autosaveRef = useRef(null);
    const autosave = useDebouncedAutosave({
        enabled: canWrite && isEditable,
        debounceMs: 1000,
        onSave: async (delta) => {
            const res = await updateEncounter(encounterId, delta);
            // Server returns the canonical encounter; merge so the client stays
            // in sync. Re-apply anything typed while this request was in flight
            // (a slow typist's trailing space lands right on the debounce
            // boundary) so the stale response can't clobber those edits.
            if (res?.data) {
                const stillPending = autosaveRef.current?.getPending() || {};
                setEncounter({ ...res.data, ...stillPending });
            }
        },
    });
    autosaveRef.current = autosave;

    const { confirmLeave } = useUnsavedGuard(autosave.hasPending);

    // ---- Data load ----
    const reload = useCallback(async () => {
        const res = await getEncounter(encounterId);
        setEncounter(res.data);
        const pid = res.data?.patientId;
        if (pid) {
            try {
                // basicInformation type returns the patient doc merged with
                // their emergency-contact record (NoK name/relation/phone)
                const p = await apiGet(`api/get-patient-data/basicInformation/${pid}`);
                const base = p.patientData || p.data || p;
                // Allergies live on the patient's medical-history record — pull
                // them in (as an array) so the alert strip + Allergies tab have
                // a data source. Best-effort: a missing record just means none.
                let allergies = [];
                try {
                    const mh = await apiGet(`api/get-patient-data/medicalHistory/${pid}`);
                    const rawAllergies = (mh?.patientData || mh?.data || {})?.allergies;
                    allergies = String(rawAllergies || '').split(',').map((s) => s.trim()).filter(Boolean);
                } catch { /* no medical-history record yet */ }
                setPatient({ ...base, allergies });
            } catch (err) {
                // patient is best-effort; banner falls back to placeholders
                console.warn('patient fetch failed', err);
            }
        }
    }, [encounterId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try { await reload(); } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [reload]);

    // Physician roster — resolves the assigned/signing physician's name in the
    // header for everyone (read-only too), and powers the inline reassign menu
    // for writers. Best-effort: a failure just leaves names unresolved.
    useEffect(() => {
        let cancelled = false;
        listAssignablePhysicians()
            .then((r) => { if (!cancelled) setPhysicians(r.data || []); })
            .catch(() => { /* header falls back to "Unassigned"/"Finalized" */ });
        return () => { cancelled = true; };
    }, []);

    // ---- Patch handler shared with every tab ----
    const patch = useCallback((delta) => {
        if (!delta || Object.keys(delta).length === 0) return;
        // Optimistic local update so typing feels instant
        setEncounter((prev) => (prev ? { ...prev, ...delta } : prev));
        autosave.queue(delta);
    }, [autosave]);

    // ---- Finalize / amend flow ----
    const openFinalize = () => setFinalizeOpen(true);
    // Signing off, whether for the first time or after an amendment: flush
    // pending edits so the signed snapshot includes them, then jump to the
    // summary so the clinician sees the PDF that just went on the record.
    const handleFinalize = async () => {
        try {
            setFinalizing(true);
            await autosave.flush();
            if (isAmending) {
                await completeAmendment(encounterId);
            } else {
                await finalizeEncounter(encounterId);
            }
            await reload();
            setFinalizeOpen(false);
            const summaryIdx = TAB_DEFS.findIndex((t) => t.key === 'summary');
            if (summaryIdx >= 0) setActive(summaryIdx);
        } catch (err) {
            console.error('finalize failed', err);
        } finally {
            setFinalizing(false);
        }
    };

    const handleAmend = async (reason) => {
        try {
            setAmending(true);
            setAmendError(null);
            await amendEncounter(encounterId, reason);
            await reload();
            setAmendOpen(false);
        } catch (err) {
            setAmendError(err.message || 'Could not open the amendment.');
        } finally {
            setAmending(false);
        }
    };

    const handleBack = () => {
        if (confirmLeave()) navigate(-1);
    };

    const tabsWithStatus = useMemo(
        () => TAB_DEFS.map((t) => ({ ...t, complete: isTabComplete(t.key, encounter, patient) })),
        [encounter, patient]
    );

    const tabKey = tabsWithStatus[Math.max(0, Math.min(active, tabsWithStatus.length - 1))].key;
    const tabDisabled = !isEditable || !canWrite;
    const tabProps = { encounter, patient, onPatch: patch, onReload: reload, disabled: tabDisabled };

    if (loading) {
        return (
            <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <CircularProgress size={28} sx={{ color: NAVY }} />
            </Box>
        );
    }
    if (!encounter) {
        return <Box sx={{ p: 4 }}>Encounter not found.</Box>;
    }

    const finalizeChecklist = buildChecklist(encounter);
    const finalizeBlocked = finalizeChecklist.some((i) => i.required && !i.satisfied);

    return (
        <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2 } }}>
            {/* Top action row. flexWrap so Back + Finalize don't clip on
                narrow phones; on desktop there's plenty of room, so wrapping
                never kicks in. */}
            <Stack
                direction="row"
                spacing={1}
                rowGap={1}
                alignItems="center"
                sx={{ mb: 1.5, flexWrap: 'wrap' }}
                justifyContent="space-between"
            >
                <Button
                    onClick={handleBack}
                    startIcon={<ArrowBackRoundedIcon />}
                    sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
                >
                    Back to patient
                </Button>
                <Stack direction="row" spacing={1}>
                    {/* Render the button for anyone granted the finalize
                        permission, but grey it out (with the reason) when
                        the role has read-only encounter access. The same
                        button closes an open amendment — it is the same act
                        (putting your name to this document), so it stays in
                        the same place rather than moving on the clinician. */}
                    {isEditable && (canFinalizePerm || isAmending) && (
                        <Tooltip
                            title={!canWrite
                                ? 'You have read-only access to encounters.'
                                : isAmending && !canAmendPerm
                                    ? 'Only staff who can amend signed reports may complete this amendment.'
                                    : finalizeBlocked
                                        ? 'Chief complaint and at least one diagnosis are required to finalize.'
                                        : ''}
                            arrow
                        >
                            <span>
                                <Button
                                    onClick={openFinalize}
                                    startIcon={<LockOutlinedIcon />}
                                    variant="contained"
                                    disabled={!canWrite || (isAmending && !canAmendPerm) /* checklist gaps enforce in the dialog; show why */}
                                    sx={{
                                        bgcolor: NAVY,
                                        boxShadow: 'none',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2.25,
                                        '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                                    }}
                                >
                                    {isAmending ? 'Complete amendment' : 'Finalize encounter'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {isFinalized && !isAmending && canAmendPerm && (
                        <Tooltip
                            title={canWrite
                                ? 'Reopen this signed report to correct it. The change is recorded.'
                                : 'You have read-only access to encounters.'}
                            arrow
                        >
                            <span>
                                <Button
                                    onClick={() => { setAmendError(null); setAmendOpen(true); }}
                                    startIcon={<HistoryEduOutlinedIcon />}
                                    variant="outlined"
                                    disabled={!canWrite}
                                    sx={{
                                        color: NAVY,
                                        borderColor: 'rgba(5,47,93,0.35)',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        px: 2.25,
                                        '&:hover': { borderColor: NAVY, bgcolor: 'rgba(5,47,93,0.04)' },
                                    }}
                                >
                                    Amend report
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {isEditable && !canWrite && (
                <Box
                    sx={{
                        mb: 1.5,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(5,47,93,0.05)',
                        border: '1px solid rgba(5,47,93,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Box component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                        You have read-only access to encounters — fields are disabled.
                    </Box>
                </Box>
            )}

            {/* An open amendment is an unusual, on-the-record state: the record
                is unlocked but the patient already has a signed report in hand.
                Say so persistently, with the stated reason, until it closes. */}
            {isAmending && (
                <Box
                    sx={{
                        mb: 1.5,
                        px: 1.75,
                        py: 1,
                        borderRadius: 1.5,
                        bgcolor: '#FFF6E5',
                        border: '1px solid #F5C26B',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                    }}
                >
                    <HistoryEduOutlinedIcon sx={{ fontSize: 18, color: '#A65300', mt: '1px' }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: '#5C3A00', display: 'block' }}>
                            Amendment in progress — this report has already been signed.
                        </Box>
                        <Box component="span" sx={{ fontSize: 13, color: '#5C3A00', display: 'block', mt: 0.25 }}>
                            Reason: {amendment.reason}
                        </Box>
                        <Box component="span" sx={{ fontSize: 12.5, color: '#7A5200', display: 'block', mt: 0.25 }}>
                            Make your corrections, then use <strong>Complete amendment</strong> to reissue the report.
                        </Box>
                    </Box>
                </Box>
            )}

            <PatientBanner
                patient={patient}
                encounter={encounter}
                isFinalized={isFinalized}
                isAmending={isAmending}
                physicians={physicians}
                canAssign={canWrite && !isFinalized}
                onAssign={(id) => patch({ assignedPhysicianId: id })}
                saveState={canWrite && isEditable ? {
                    status: autosave.status,
                    lastSavedAt: autosave.lastSavedAt,
                    error: autosave.error,
                    onRetry: autosave.flush,
                } : null}
            />

            {/* Editor body — responsive: rail+panel on desktop, stacked on tablet/mobile */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    alignItems: 'flex-start',
                }}
            >
                <TabRail
                    tabs={tabsWithStatus}
                    activeIndex={active}
                    onChange={setActive}
                />
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: '100%',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        p: { xs: 2, md: 3.5 },
                        bgcolor: '#fff',
                        boxShadow: '0 1px 2px rgba(5,47,93,0.04)',
                    }}
                >
                    {tabKey === 'patient' && <PatientInfoTab patient={patient} />}
                    {tabKey === 'allergies' && <AllergiesTab {...tabProps} />}
                    {tabKey === 'vitals' && <VitalsTab {...tabProps} />}
                    {tabKey === 'chiefComplaint' && (
                        <SimpleTextTab title="Chief complaint" field="chiefComplaint" {...tabProps}
                            placeholder="What brings the patient in today?" rows={4} />
                    )}
                    {tabKey === 'hopi' && (
                        <SimpleTextTab title="History of presenting illness" field="historyOfPresenting" {...tabProps}
                            placeholder="Onset, character, duration, alleviating/aggravating factors…" rows={10} />
                    )}
                    {tabKey === 'currentMedications' && (
                        <ChipListTab title="Current medication" field="currentMedications" {...tabProps}
                            placeholder="Type a medication and press Enter"
                            denialLabel="No current medication"
                            denialValue="No current medications" />
                    )}
                    {tabKey === 'pmh' && (
                        <ChipListTab title="Past medical history" field="pastMedicalHistory" {...tabProps}
                            placeholder="Add condition and press Enter"
                            denialLabel="No past medical history"
                            denialValue="No significant past medical history" />
                    )}
                    {tabKey === 'psh' && (
                        <ChipListTab title="Past surgical history" field="pastSurgicalHistory" {...tabProps}
                            placeholder="Add procedure and press Enter"
                            denialLabel="No prior surgery"
                            denialValue="No previous surgeries" />
                    )}
                    {tabKey === 'previousInvestigations' && <PreviousInvestigationsTab {...tabProps} />}
                    {tabKey === 'documents' && <DocumentsTab {...tabProps} />}
                    {tabKey === 'physicalExam' && <PhysicalExamTab {...tabProps} />}
                    {tabKey === 'diagnoses' && <DiagnosisTab {...tabProps} />}
                    {tabKey === 'investigations' && <InvestigationTab {...tabProps} />}
                    {tabKey === 'treatment' && <TreatmentTab {...tabProps} />}
                    {tabKey === 'workStatus' && <WorkStatusTab {...tabProps} />}
                    {tabKey === 'summary' && (
                        <SummaryTab
                            encounter={encounter}
                            patient={patient}
                            physicians={physicians}
                            onFlush={canWrite && isEditable ? autosave.flush : undefined}
                        />
                    )}
                </Paper>
            </Box>

            <FinalizeDialog
                open={finalizeOpen}
                encounter={encounter}
                onClose={() => setFinalizeOpen(false)}
                onConfirm={handleFinalize}
                submitting={finalizing}
                amending={isAmending}
            />

            <AmendDialog
                open={amendOpen}
                onClose={() => setAmendOpen(false)}
                onConfirm={handleAmend}
                submitting={amending}
                error={amendError}
            />
        </Container>
    );
}
